import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const RELATIONSHIPS = [
  '本人', '配偶者', '父', '母', '息子', '娘',
  '祖父', '祖母', '孫息子', '孫娘',
  '兄', '姉', '弟', '妹',
  '義父', '義母', '義兄', '義姉', '義弟', '義妹',
  '義祖父', '義祖母',
];

const CondolenceInvite = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [storeCode, setStoreCode] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [relationship, setRelationship] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [ownStoreCode, setOwnStoreCode] = useState('');
  const [ownStoreName, setOwnStoreName] = useState('');
  const [copyOk, setCopyOk] = useState(false);
  const [copiedToken, setCopiedToken] = useState('');

  useEffect(() => {
    fetch('/api/condolence/check', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) { navigate('/condolence'); return; }
        setIsAdmin(!!data.user?.is_admin);
        if (data.user?.id) {
          setOwnStoreCode(String(data.user.id));
          setOwnStoreName(data.user.department || '');
          setStoreCode(String(data.user.id));
        }
      });

    fetch('/api/condolence/stores', { credentials: 'include' })
      .then(res => res.json())
      .then(data => setStores(data.stores || []))
      .catch(() => {});

    loadHistory();
  }, [navigate]);

  const loadHistory = useCallback(() => {
    fetch('/api/condolence/invitations', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setHistory(data.invitations || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!storeCode) {
      setEmployees([]);
      setEmployeeNumber('');
      return;
    }
    fetch(`/api/condolence/employee-master?store_code=${encodeURIComponent(storeCode)}`, {
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        setEmployees(data.employees || []);
        setEmployeeNumber('');
      })
      .catch(() => setEmployees([]));
  }, [storeCode]);

  const selectedEmployee = employees.find(e => e.employee_number === employeeNumber);

  const canSend = storeCode && employeeNumber && relationship;
  const hasEmail = !!selectedEmployee?.email;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError('');
    setResult(null);
    setCopyOk(false);
    try {
      const res = await fetch('/api/condolence/invitations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ employee_number: employeeNumber, relationship }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        loadHistory();
        setRelationship('');
        setEmployeeNumber('');
      } else {
        setError(data.error || '送信に失敗しました');
      }
    } catch {
      setError('通信エラーが発生しました');
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.submit_url) return;
    try {
      await navigator.clipboard.writeText(result.submit_url);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {}
  };

  // 発行履歴の各行のURLをコピー（「メールが届かない」連絡時に手渡す用）
  const copyRowUrl = async (url, token) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(''), 2000);
    } catch {}
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const z = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${z(d.getMonth() + 1)}/${z(d.getDate())} ${z(d.getHours())}:${z(d.getMinutes())}`;
  };

  const visibleStores = isAdmin
    ? stores
    : stores.filter(s => String(s.store_code) === ownStoreCode);

  return (
    <div className="form-page">
      <header className="form-header" style={{ background: '#37474f' }}>
        <button className="back-button" onClick={() => navigate('/condolence/menu')}>← 戻る</button>
        <h1>弔意URL発行</h1>
      </header>

      <main className="form-main">
        <div className="section-bar" style={{ background: '#eceff1' }}>
          <span className="icon">✉️</span>
          <span className="title" style={{ color: '#37474f' }}>従業員へ入力フォームURLをメール送信</span>
        </div>

        <div className="form-card">
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
            ※ 従業員から連絡を受けた際に、所属店舗・従業員・続柄を入力して「メール送信」を押すと、
            従業員のメールアドレス宛に入力フォームのURLが届きます（有効期限：3日）。<br />
            ※ メールアドレスが未登録の場合は、URLが画面に表示されるのでSMSやLINE等で共有してください。
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px 16px', alignItems: 'center', marginBottom: '12px' }}>
            <label>所属店舗</label>
            {isAdmin ? (
              <select value={storeCode} onChange={(e) => setStoreCode(e.target.value)} style={selStyle}>
                <option value="">選択してください</option>
                {visibleStores.map(s => (
                  <option key={s.store_code} value={s.store_code}>
                    {s.store_code} {s.store_name}
                  </option>
                ))}
              </select>
            ) : (
              <div style={readOnlyBox}>
                {ownStoreCode} {ownStoreName}
              </div>
            )}

            <label>従業員</label>
            <select
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              disabled={!storeCode}
              style={selStyle}
            >
              <option value="">{storeCode ? '選択してください' : '先に店舗を選択'}</option>
              {employees.map(e => (
                <option key={e.employee_number} value={e.employee_number}>
                  {e.name}（{e.employee_number}）{!e.email ? ' [メール未登録]' : ''}
                </option>
              ))}
            </select>

            <label>従業員番号</label>
            <div style={readOnlyBox}>{selectedEmployee?.employee_number || '—'}</div>

            <label>区分</label>
            <div style={readOnlyBox}>{selectedEmployee?.category || '—'}</div>

            <label>メールアドレス</label>
            <div style={readOnlyBox}>
              {selectedEmployee
                ? (selectedEmployee.email || <span style={{ color: '#e65100' }}>未登録（URL発行後、SMS/LINE等で共有してください）</span>)
                : '—'}
            </div>

            <label>従業員からみた死亡者の続柄</label>
            <select value={relationship} onChange={(e) => setRelationship(e.target.value)} style={selStyle}>
              <option value="">選択してください</option>
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <div />
            <div style={{ fontSize: '13px', color: '#555', lineHeight: 1.6, marginTop: '-4px' }}>
              選択肢にない続柄の場合は弔慰金、訃報、弔電、供花はありませんので、申請は不要です。
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          {result && result.success && result.email_status === 'sent' && (
            <div style={{
              background: '#e8f5e9', border: '1px solid #66bb6a', borderRadius: '6px',
              padding: '12px', marginBottom: '12px', color: '#2e7d32', fontSize: '14px',
            }}>
              ✔ {result.email_to} 宛にメール送信しました
            </div>
          )}

          {result && result.success && result.email_status === 'no_email' && (
            <div style={{
              background: '#fff8e1', border: '1px solid #ffb74d', borderRadius: '6px',
              padding: '14px', marginBottom: '12px', color: '#bf360c', fontSize: '14px',
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                ⚠ メールアドレスが未登録の為、下記URLをSMSやLINEなどで共有してください。
              </div>
              <div style={{
                background: '#fff', border: '1px solid #ffcc80', borderRadius: '4px',
                padding: '10px', marginBottom: '8px', wordBreak: 'break-all',
                fontSize: '13px', color: '#37474f', fontFamily: 'monospace',
              }}>
                {result.submit_url}
              </div>
              <button
                type="button"
                onClick={handleCopy}
                style={{
                  background: copyOk ? '#66bb6a' : '#ff9800',
                  color: 'white', border: 'none', padding: '8px 16px',
                  borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                }}
              >
                {copyOk ? '✔ コピーしました' : 'URLをコピー'}
              </button>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                有効期限: 発行から3日間
              </div>
            </div>
          )}

          {result && result.success && result.email_status === 'failed' && (
            <div style={{
              background: '#fff8e1', border: '1px solid #ffb74d', borderRadius: '6px',
              padding: '14px', marginBottom: '12px', color: '#bf360c', fontSize: '14px',
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                ⚠ メール送信に失敗しました。下記URLをSMSやLINEなどで共有してください。
              </div>
              <div style={{
                background: '#fff', border: '1px solid #ffcc80', borderRadius: '4px',
                padding: '10px', marginBottom: '8px', wordBreak: 'break-all',
                fontSize: '13px', color: '#37474f', fontFamily: 'monospace',
              }}>
                {result.submit_url}
              </div>
              <button
                type="button"
                onClick={handleCopy}
                style={{
                  background: copyOk ? '#66bb6a' : '#ff9800',
                  color: 'white', border: 'none', padding: '8px 16px',
                  borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                }}
              >
                {copyOk ? '✔ コピーしました' : 'URLをコピー'}
              </button>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                有効期限: 発行から3日間<br />
                <span style={{ color: '#999' }}>送信エラー詳細: {result.email_error}</span>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend || sending}
            style={{
              background: !canSend || sending ? '#aaa' : '#37474f',
              color: 'white', border: 'none', padding: '12px 24px',
              borderRadius: '6px', cursor: !canSend || sending ? 'not-allowed' : 'pointer',
              fontSize: '16px', width: '100%',
            }}
          >
            {sending ? '送信中...' : (hasEmail ? 'メール送信' : 'URL発行（メール送信なし）')}
          </button>
        </div>

        <div className="section-bar" style={{ background: '#eceff1', marginTop: '24px' }}>
          <span className="icon">📜</span>
          <span className="title" style={{ color: '#37474f' }}>自分が発行した履歴（最新50件）</span>
        </div>

        <div className="form-card">
          {history.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999' }}>まだ発行履歴がありません</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#eceff1' }}>
                    <th style={th}>発行日時</th>
                    <th style={th}>従業員</th>
                    <th style={th}>続柄</th>
                    <th style={th}>送信先</th>
                    <th style={th}>状態</th>
                    <th style={th}>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.token}>
                      <td style={td}>{formatDateTime(h.created_at)}</td>
                      <td style={td}>{h.employee_name}（{h.employee_number}）</td>
                      <td style={td}>{h.relationship}</td>
                      <td style={td}>{h.email || <span style={{ color: '#e65100' }}>未登録(URL共有)</span>}</td>
                      <td style={td}>
                        {h.used_at
                          ? <span style={{ color: '#2e7d32' }}>提出済 {formatDateTime(h.used_at)}</span>
                          : h.expired
                            ? <span style={{ color: '#d32f2f' }}>期限切れ</span>
                            : <span style={{ color: '#1976d2' }}>送信済（期限 {formatDateTime(h.expires_at)}）</span>
                        }
                      </td>
                      <td style={td}>
                        {h.used_at || h.expired || !h.submit_url
                          ? <span style={{ color: '#bbb' }}>—</span>
                          : (
                            <button
                              type="button"
                              onClick={() => copyRowUrl(h.submit_url, h.token)}
                              title={h.submit_url}
                              style={{
                                padding: '3px 10px', fontSize: '12px', cursor: 'pointer',
                                border: '1px solid #90a4ae', borderRadius: '4px',
                                background: copiedToken === h.token ? '#2e7d32' : '#fff',
                                color: copiedToken === h.token ? '#fff' : '#37474f',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {copiedToken === h.token ? '✓ コピー済' : 'URLをコピー'}
                            </button>
                          )
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const selStyle = {
  padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px',
};
const readOnlyBox = {
  padding: '10px', background: '#f5f5f5', border: '1px solid #eee', borderRadius: '6px',
  fontSize: '14px', color: '#37474f',
};
const th = {
  padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #cfd8dc',
  color: '#37474f', fontWeight: 'bold',
};
const td = {
  padding: '6px 12px', borderBottom: '1px solid #eceff1',
};

export default CondolenceInvite;
